import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
} from "@mui/material";
import PDFCanvas, { Annotation, PDFInfo } from "../PDFCanvas";
import {
  backend,
  FormFieldValuesUI,
  LensHit,
  makeLensHitFilters,
  pageFromIntakePageKey,
  processCardItem,
} from "Backend";
import Flex from "../Flex";
import {
  Add,
  CheckCircle,
  Close,
  Comment,
  CopyAll,
  Create,
  Download,
  MoreVert,
  PushPin,
  Rotate90DegreesCcw,
  ViewSidebar,
  Visibility,
} from "@mui/icons-material";
import useDebounce from "helpers/useDebounce";
import { Paginator } from "./Paginator";
import BiotechIcon from "@mui/icons-material/Biotech";
import theme from "theme";
import PushPinIcon from "@mui/icons-material/PushPin";
import LoadingPattern from "../LoadingPattern";
import ZoomControls from "./ZoomControls";
import { Transition } from "react-transition-group";
import CardFormFragment, { CardFormFragmentProps } from "routes/lenses/lens/case/HitCard/CardFormFragment";
import { useSnackbar } from "notistack";
import { Block, DocumentAnnotation, DocumentIntake, DocumentSegment } from "generated/graphql";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  ensurePageLensHits,
  findLensHitFromBlock,
  selectDocumentSegmentPageCache,
  selectPageStatus,
} from "redux/slices/intakeViewer";
import RequestStatus from "redux/slices/RequestStatus";
import { collect } from "helpers/coll";
import { CaseFormContext } from "contexts/CaseFormContext";
import { useFormCard } from "routes/lenses/lens/case/useFormCard";
import PinToProfileButton from "routes/lenses/lens/case/HitCard/PinToProfileButton";
import PinForm from "./PinForm";
import { selectIsSupport, selectSavedZoom } from "../../redux/slices/session";
import produce from "immer";
import AnnotationSidebar from "./AnnotationSidebar";
import useResizeObserver from "helpers/hooks/useResizeObserver";
import useAnnotations from "helpers/hooks/useAnnotations";
import { AnnotationsContext } from "contexts/AnnotationsContext";
import AnnotationEditor from "./AnnotationEditor";
import { P } from "theme/typography";
import { primary } from "theme/palette";
import PDFCanvas3 from "components/PDFCanvas3";
import { PageCallback } from "react-pdf/dist/cjs/shared/types.d.ts";
import { findDocumentSegmentInPageRange } from "redux/slices/case";
import { useZoomControls } from "./useZoomControls";
import { usePopover } from "helpers/hooks/usePopover";

interface PageRefMap {
  [intakePageNumber: number]: HTMLElement;
}

interface DragCoordinates {
  x: number;
  y: number;
}

const blockToAnnotation = (block: Block, color: string): Annotation => {
  return {
    top: block.boundingBox.top,
    left: block.boundingBox.left,
    width: block.boundingBox.width,
    height: block.boundingBox.height,
    color,
  };
};

export interface IntakeViewerProps {
  className?: string;
  lensId: number;
  caseId: number;
  intake: DocumentIntake;
  parentBlockIndex?: number | null;
  parentLensHit?: LensHit;
  pageNumberHandle: [page: number, setPage: (value: number) => void];
  additionalAnnotations: Annotation[];
  duplicateBlocksForHit?: number[];
  canPin?: boolean;
  canPinFullPage?: boolean;
  canShowAllHits?: boolean;
  onFormUpdate?: () => Promise<FormFieldValuesUI[] | undefined>;
  onPinToPage?: (showPinForm: boolean) => void;
  knownFieldId?: number;
  lensHitFilterId?: number;
  defaultShowHighlightLensHits?: boolean;
  pinForm?: {
    cardFormFragmentProps?: Partial<CardFormFragmentProps>;
    mode: "drawer" | "panel";
  };
  ignoreExistingBlocksForPinning?: boolean;
  immediatePinToValueGroup?: (block: Block) => void;
}

const IntakeViewer = (props: IntakeViewerProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { debugSegmentIntake, docViewerScroll } = useFlags();
  const [intakeViewerRef, setIntakeViewerRef] = useState<HTMLElement | undefined>(undefined);
  const {
    lensId,
    caseId,
    intake,
    parentBlockIndex,
    parentLensHit,
    knownFieldId,
    additionalAnnotations,
    duplicateBlocksForHit,
    onPinToPage,
    lensHitFilterId,
    ignoreExistingBlocksForPinning = false,
    immediatePinToValueGroup,
  } = props;
  const dispatch = useAppDispatch();
  const caseForm = useContext(CaseFormContext);
  const canShowAllHits = props.canShowAllHits !== false;
  const canPin = props.canPin !== false;
  const [pageNum, setPageNum] = props.pageNumberHandle;
  const intakePageNumKey = useMemo(() => `${intake.id}:${pageNum}`, [intake.id, pageNum]);
  const intakePageNumKeyDebounced = useDebounce(intakePageNumKey, 200);
  const pageNumDebounced = useMemo(() => pageFromIntakePageKey(intakePageNumKeyDebounced), [intakePageNumKeyDebounced]);
  const pageNumNOTDebounced = pageFromIntakePageKey(intakePageNumKey);
  const [highlightLensHits, setHighlightLensHits] = useState(props.defaultShowHighlightLensHits === true);
  const [qaMode, setQAMode] = useState(false);
  const isSupport = useAppSelector(selectIsSupport);
  const [visiblePageNumber, setVisiblePageNumber] = useState<number | undefined>(undefined);
  const savedZoom = useAppSelector(selectSavedZoom);
  const documentPopover = usePopover();
  const currentSegment: DocumentSegment | undefined = useMemo(() => {
    const seg = findDocumentSegmentInPageRange(intake, pageNumNOTDebounced);
    if (debugSegmentIntake) {
      // eslint-disable-next-line no-console
      console.log(`currentSegmentIndex ${seg?.partIndex} ${intake.id} ${pageNumNOTDebounced}`);
      // eslint-disable-next-line no-console
      console.log(seg);
    }
    return seg;
  }, [intakePageNumKey]);

  const segmentUrl: string | undefined = useMemo(() => {
    return currentSegment ? backend.getDocumentUrl(currentSegment.id) : undefined;
  }, [currentSegment]);

  const segmentPageNum = currentSegment ? pageNumNOTDebounced - currentSegment.startPage + 1 : pageNumNOTDebounced;

  const zoomControls = useZoomControls(savedZoom);
  const [pageProxy, setPageProxy] = useState<PageCallback | undefined>(undefined);
  const docViewerRef = useRef<HTMLElement>();

  const debouncedDocViewerSize = useResizeObserver(docViewerRef.current);

  const [, setPdfInfo] = useState<PDFInfo | undefined>(undefined);
  const pageRotateState = useState(0);
  const annotations = useAnnotations({
    documentId: currentSegment?.id,
    pageNumber: segmentPageNum,
  });

  const [hoveredAnnotation, setHoveredAnnotation] = useState<DocumentAnnotation | undefined>(undefined);
  const [hovered, setHovered] = useState<number | undefined>(undefined);
  const pageCache = useAppSelector((state) =>
    selectDocumentSegmentPageCache(state, [currentSegment?.id, segmentPageNum])
  );
  const pageStatus = useAppSelector(selectPageStatus);
  const foundReferenceOnCurrentPage = useMemo(() => {
    return (caseForm?.allFieldReferences || []).find((ref) => {
      if (ref.documentId !== currentSegment?.id) return false;

      return ref.pageRangeStart && ref.pageRangeEnd
        ? ref.pageRangeStart <= pageNum && ref.pageRangeEnd >= pageNum
        : ref.originalPage === pageNum;
    });
  }, [caseForm?.allFieldReferences, pageNum, currentSegment]);
  const [chosenBlock, setChosenBlock] = useState<Block | undefined>();

  const selectedBlockIndex = chosenBlock ? chosenBlock.index : parentBlockIndex;

  const [contextMenuOpen, setContextMenuOpen] = useState(false);
  const [contextMenuCoords, setContextMenuCoords] = React.useState<{ left: number; top: number }>();
  const [clickedBlock, setClickedBlock] = useState<Block | undefined>();
  const currentLensHit = chosenBlock
    ? findLensHitFromBlock(pageCache?.lensRun?.hits || [], chosenBlock)
    : parentLensHit;

  const blockForForm = chosenBlock
    ? chosenBlock
    : parentLensHit?.block.page === pageNum
      ? currentLensHit?.block
      : undefined;

  const formCard = useFormCard({
    lensId,
    caseId,
    intakeId: intake.id,
    intakePage: blockForForm?.originalPageNumber || pageNumNOTDebounced,
    blockIndex: blockForForm?.index,
    initialFieldId: knownFieldId,
  });
  const [pinMode, setPinMode] = formCard.formModeState;
  const [showAnnotationSidebar, setShowAnnotationSidebar] = useState(false);
  const [documentViewerPageRefs, setDocumentViewerPageRefs] = useState<PageRefMap>({});
  const [canvasScrollContainer, setCanvasScrollContainer] = useState<HTMLElement | undefined>(undefined);
  const [pdfContainerRef, setPDFContainerRef] = useState<HTMLElement | undefined>(undefined);
  const [canDrag, setCanDrag] = useState(false);
  const dragStartCoordinates = useRef<DragCoordinates>({ x: 0, y: 0 });
  const canvasScrollContainerSet = useCallback((ref: HTMLElement) => {
    setCanvasScrollContainer(ref);
  }, []);

  const pdfContainerRefSet = useCallback(
    (node: HTMLDivElement) => {
      node?.focus();
      setPDFContainerRef(node);
    },
    // Using pageProxy as a dependency helps focus in the EntryViewer,
    // we will re-focus the node so keyboard bindings work whenever you paginate
    // Could be a separate useEffect hook but just combining here so we get the
    // onLoad behavior as well
    [pageProxy]
  );

  const addDocumentViewerPageRef = (intakePageNumber: number, ref: HTMLElement): void => {
    setDocumentViewerPageRefs((prev) =>
      produce(prev, (draft: PageRefMap) => {
        draft[intakePageNumber] = ref;
      })
    );
  };

  const removeDocumentViewerPageRef = (intakePageNumber: number): void => {
    setDocumentViewerPageRefs((prev) =>
      produce(prev, (draft: PageRefMap) => {
        delete draft[intakePageNumber];
      })
    );
  };

  const goToPage = (intakePageNumber: number): void => {
    const ref = documentViewerPageRefs[intakePageNumber];
    setPageNum(intakePageNumber);
    if (docViewerScroll) {
      setVisiblePageNumber(intakePageNumber);
    }
    if (ref) {
      ref.scrollIntoView({
        behavior: "auto",
      });
    }
  };

  const intakeViewerRefSet = useCallback((ref: HTMLDivElement) => {
    setIntakeViewerRef(ref);
  }, []);

  const resetHighlights = () => {
    formCard.multipleReferencesState[1](false);
    setChosenBlock(undefined);
    formCard.notesHandle[1]("");
    formCard.selectedValueGroupState[1](-1);
  };

  const handlePinPage = (showPinForm: boolean) => {
    if (onPinToPage) {
      onPinToPage(showPinForm);
    } else {
      setPinMode(showPinForm);
      resetHighlights();
    }
  };

  useEffect(() => {
    const effect = async () => {
      if (pageNumDebounced === undefined || !currentSegment) {
        return;
      }
      dispatch(
        ensurePageLensHits({
          lensId,
          caseId,
          offset: 0,
          limit: 1000,
          includeSurroundings: false,
          filters: makeLensHitFilters({}),
          pageNumber: segmentPageNum,
          documentSegmentId: currentSegment.id,
        })
      );
    };
    effect();
  }, [lensId, caseId, currentSegment?.id, pageNumDebounced]);

  useEffect(() => {
    if (visiblePageNumber !== undefined) goToPage(visiblePageNumber);
  }, [pageNumDebounced, documentViewerPageRefs, visiblePageNumber]);

  useEffect(() => {
    pageRotateState[1](0);
  }, [intake]);

  useEffect(() => {
    setChosenBlock(undefined);
  }, [parentBlockIndex, parentLensHit?.hitId]);

  const blocksByIndex = useMemo(() => new Map(annotations.blocks.map((b) => [b.index, b])), [annotations.blocks]);
  const pinnedBlocksOnPage: Block[] = useMemo(() => {
    return collect(caseForm?.allFieldReferences || [], (ref) =>
      ref.documentId === currentSegment?.id &&
      ref.originalPage === pageNum &&
      ref.blockIndex !== undefined &&
      ref.blockIndex !== null
        ? blocksByIndex.get(ref.blockIndex)
        : undefined
    );
  }, [pageNum, caseForm?.allFieldReferences, blocksByIndex, currentSegment]);

  const allReferenceBlocks: Block[] = useMemo(() => {
    const blockByIndex =
      selectedBlockIndex !== undefined && selectedBlockIndex !== null
        ? blocksByIndex.get(selectedBlockIndex)
        : undefined;
    // the chosen block may not be on the current page you're viewing so don't highlight the wrong block
    // even if they share the same index value
    const selectedBlock =
      blockByIndex && blockByIndex.originalPageNumber === pageNumDebounced ? blockByIndex : undefined;

    return formCard.multipleReferencesState[0]
      ? (pageCache?.lensRun?.hits || [])
          .filter(
            (hit: LensHit) =>
              `${hit.ruleSubtype}${hit.ruleDescription}` ===
              `${currentLensHit?.ruleSubtype}${currentLensHit?.ruleDescription}`
          )
          .map((hit: LensHit) => hit.block)
      : selectedBlock !== undefined
        ? [selectedBlock]
        : [];
  }, [
    formCard.multipleReferencesState[0],
    pageCache?.lensRun?.hits,
    currentLensHit,
    selectedBlockIndex,
    blocksByIndex,
    pageNumDebounced,
  ]);

  const pdfContainerHasScroll =
    pdfContainerRef !== undefined && pdfContainerRef !== null
      ? pdfContainerRef.scrollHeight > pdfContainerRef.clientHeight ||
        pdfContainerRef.scrollWidth > pdfContainerRef.clientWidth
      : false;

  // Filter out selected block
  // Choose 1 lens hit per block
  const blockAnnotations = useMemo(() => {
    const selectedBlockAnnotations: [number, Annotation][] = allReferenceBlocks.map((block) => [
      block.index,
      {
        ...blockToAnnotation(block, "yellow"),
        pinned:
          (caseForm?.allFieldReferences || []).findIndex((reference) => {
            return reference.documentId === currentSegment?.id && reference.blockIndex === block.index;
          }) > -1,
      },
    ]);

    const duplicateBlockAnnotations: [number, Annotation][] = (pageCache?.lensRun.hits || [])
      .filter((hit: LensHit) => duplicateBlocksForHit?.find((blockIndex) => hit.block.index === blockIndex))
      .map((hit: LensHit) => [
        hit.block.index,
        {
          ...blockToAnnotation(hit.block, "yellow"),
          pinned:
            (caseForm?.allFieldReferences || []).findIndex((reference) => {
              return reference.documentId === currentSegment?.id && reference.blockIndex === hit.block.index;
            }) > -1,
        },
      ]);

    const lensHitAnnotations: [number, Annotation][] =
      pageStatus === RequestStatus.PENDING || !highlightLensHits || formCard.multipleReferencesState[0]
        ? []
        : (pageCache?.lensRun?.hits || [])
            .filter(
              (hit: LensHit) =>
                (lensHitFilterId ? hit.formFieldId === lensHitFilterId : true) &&
                pinnedBlocksOnPage.findIndex((block) => block.index === hit.block.index) === -1
            )
            .map((hit: LensHit) => [
              hit.block.index,
              {
                ...blockToAnnotation(hit.block, theme.palette.primary.light),
                pinned:
                  (caseForm?.allFieldReferences || []).findIndex((reference) => {
                    return reference.documentId === currentSegment?.id && reference.blockIndex === hit.block.index;
                  }) > -1,
              },
            ]);

    const pinnedAnnotations: [number, Annotation][] = pinnedBlocksOnPage
      .filter((block) => allReferenceBlocks.findIndex((rb) => rb.index === block.index) === -1)
      .map((block) => [
        block.index,
        {
          ...blockToAnnotation(block, theme.palette.success.light),
          pinned: true,
        },
      ]);

    const clusterTypeAnnotations: [number, Annotation][] = qaMode
      ? collect(annotations.blocks, (block) => {
          if (block.clusterType) {
            return [
              block.index,
              { ...blockToAnnotation(block, theme.palette.grey[100]), label: `${block.index} ${block.clusterType}` },
            ];
          }
        })
      : [];

    const tableAnnotations: Annotation[] = qaMode
      ? annotations.tables
          .map((table) => {
            return table.cells.map<Annotation>((cell) => ({
              top: cell.boundingBox.top,
              left: cell.boundingBox.left,
              width: cell.boundingBox.width,
              height: cell.boundingBox.height,
              color: cell.tags.includes("COLUMN_HEADER") ? "cyan" : cell.columnIndex % 2 === 0 ? "gray" : "green",
              pinned: false,
            }));
          })
          .flat(1)
      : [];

    const annotationsByBlockMap = new Map<number, Annotation>([
      ...lensHitAnnotations,
      ...selectedBlockAnnotations,
      ...duplicateBlockAnnotations,
      ...pinnedAnnotations,
      ...clusterTypeAnnotations,
    ]);
    const annotationsByBlock = [...annotationsByBlockMap.values()];
    return annotationsByBlock.concat(additionalAnnotations, tableAnnotations);
  }, [
    caseForm?.allFieldReferences,
    formCard.multipleReferencesState[0],
    allReferenceBlocks,
    parentBlockIndex,
    pageStatus,
    highlightLensHits,
    additionalAnnotations,
    chosenBlock?.index,
    lensHitFilterId,
    pinnedBlocksOnPage,
    duplicateBlocksForHit,
    qaMode,
  ]);

  const hoverAnnotation =
    hovered !== undefined
      ? (() => {
          const hoveredBlock = blocksByIndex.get(hovered);
          return hoveredBlock ? [blockToAnnotation(hoveredBlock, theme.palette.secondary.light)] : [];
        })()
      : [];

  const downloadIntake = () => {
    const url = backend.getIntakeDownloadUrl(intake.id);
    window.open(url, "_blank");
  };

  const onDragStart = (x: number, y: number) => {
    if (canDrag) {
      dragStartCoordinates.current.x = x;
      dragStartCoordinates.current.y = y;
      document.body.style.cursor = "grabbing";
    }
  };

  const onDragEnd = (x: number, y: number) => {
    if (canDrag) {
      const xOffset = x - dragStartCoordinates.current.x;
      const yOffset = y - dragStartCoordinates.current.y;
      if (pdfContainerRef) {
        pdfContainerRef.scrollBy(xOffset, yOffset);
      }
    }
    document.body.style.cursor = "default";
  };

  const onDrag = (x: number, y: number) => {
    if (canDrag) {
      const xOffset = dragStartCoordinates.current.x - x;
      const yOffset = dragStartCoordinates.current.y - y;
      if (pdfContainerRef) {
        pdfContainerRef.scrollBy(xOffset, yOffset);
        dragStartCoordinates.current.x = x;
        dragStartCoordinates.current.y = y;
      }
    }
  };

  const onCanvasHover = (x: number, y: number) => {
    const intersect = annotations.blocks.find((b) => {
      // Bounding box intersection
      const box = b.boundingBox;
      const yBound = y > box.top && y < box.top + box.height;
      const xBound = x > box.left && x < box.left + box.width;
      return yBound && xBound;
    });
    setHovered(intersect?.index);

    if (annotations.selectedDocumentAnnotation === undefined) {
      const hoveredAnnotation = annotations.documentAnnotations.find((annotation) => {
        const yBound = y > annotation.boundingBox.top && y < annotation.boundingBox.top + annotation.boundingBox.height;
        const xBound =
          x > annotation.boundingBox.left && x < annotation.boundingBox.left + annotation.boundingBox.width;
        return yBound && xBound;
      });
      if (hoveredAnnotation && (highlightLensHits || showAnnotationSidebar)) {
        setHoveredAnnotation(hoveredAnnotation);
        document.body.style.cursor = "pointer";
        return;
      } else {
        setHoveredAnnotation(undefined);
      }
    }

    if (!annotations.drawing.enabled) {
      if (intersect) {
        document.body.style.cursor = "pointer";
        setCanDrag(false);
      } else {
        if (pdfContainerHasScroll) {
          document.body.style.cursor = "grab";
          setCanDrag(true);
        } else {
          document.body.style.cursor = "default";
        }
      }
    }
  };

  const onClick = ({ pixelX, pixelY }) => {
    setContextMenuCoords({ top: pixelY + 15, left: pixelX + 15 });
    const hoveredBlock = hovered !== undefined ? blocksByIndex.get(hovered) : undefined;

    pdfContainerRef?.focus();

    if (hoveredBlock) {
      setClickedBlock(hoveredBlock);
      setContextMenuOpen(true);
      document.body.style.cursor = "default";
    } else if (hoveredAnnotation && !annotations.selectedDocumentAnnotation) {
      annotations.setSelectedDocumentAnnotation(hoveredAnnotation);
      annotations.setPopoverOpen(true);
    }
  };

  const toggleFormMode = async () => {
    const isSave = pinMode;
    await formCard.saveFormValue(
      ignoreExistingBlocksForPinning
        ? []
        : allReferenceBlocks.filter((block) => block.index !== blockForForm?.index).map((block) => block.index)
    );

    resetHighlights();

    if (caseForm) {
      caseForm.resetAllFieldsServer();
    }

    if (props.onFormUpdate && isSave) {
      await props.onFormUpdate();
    }
  };

  const renderDocument = () => {
    return currentSegment && segmentUrl ? (
      docViewerScroll ? (
        <Box
          data-sentry-block
          ref={canvasScrollContainerSet}
          sx={{ overflow: "auto", flexGrow: 1, backgroundColor: "grey.100", zIndex: pinMode ? theme.zIndex.modal : 0 }}
          onClick={(event) => {
            onClick({ pixelX: event.clientX, pixelY: event.clientY });
          }}>
          <PDFCanvas3
            canvasScrollContainer={canvasScrollContainer}
            setVisiblePageNumber={setVisiblePageNumber}
            visiblePageNumber={visiblePageNumber}
            pageProxy={pageProxy}
            addDocumentViewerPageRef={addDocumentViewerPageRef}
            removeDocumentViewerPageRef={removeDocumentViewerPageRef}
            intakePageNumber={pageNum}
            setPageNumber={setPageNum}
            intake={intake}
            pdfUrl={segmentUrl}
            pdfSize={currentSegment.sizeBytes}
            segment={currentSegment}
            setPageProxy={setPageProxy}
            scale={zoomControls.scale}
            userRotateState={pageRotateState}
            annotations={blockAnnotations.concat(hoverAnnotation)}
            pageNumber={segmentPageNum}
            onLoad={setPdfInfo}
            onHover={onCanvasHover}
          />
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            zIndex: pinMode ? theme.zIndex.modal : 0,
            display: "grid",
            placeItems: "center",
            height: "100%",
          }}
          data-sentry-block
          onClick={(event) => {
            onClick({ pixelX: event.clientX, pixelY: event.clientY });
          }}>
          <PDFCanvas
            blocks={annotations.blocks}
            pdfUrl={segmentUrl}
            pdfSize={currentSegment.sizeBytes}
            pageProxy={pageProxy}
            setPageProxy={setPageProxy}
            scale={zoomControls.scale}
            userRotateState={pageRotateState}
            annotations={blockAnnotations.concat(hoverAnnotation)}
            showAnnotations={highlightLensHits || showAnnotationSidebar}
            pageNumber={segmentPageNum}
            onLoad={setPdfInfo}
            onHover={onCanvasHover}
            onDragEnd={onDragEnd}
            onDragStart={onDragStart}
            onDrag={onDrag}
            onPageChanged={({ segmentNumber, pageNumber }) => {
              backend.markDocumentPageViewed(segmentNumber, pageNumber);
            }}
            segmentId={currentSegment.id}
          />
        </Box>
      )
    ) : (
      <LoadingPattern centered sx={{ flexGrow: 1 }} />
    );
  };

  const rotate90CCW = () => {
    pageRotateState[1]((prev) => (prev === 0 ? 270 : prev - 90));
  };

  const processedCardItem = currentLensHit ? processCardItem(currentLensHit) : undefined;

  return (
    <AnnotationsContext.Provider value={annotations}>
      <div
        className={`flex flex-col overflow-hidden h-full w-full bg-white rounded-lg shadow ${props.className || ""}`}
        ref={intakeViewerRefSet}>
        {pinMode && intakeViewerRef !== undefined && props.pinForm?.mode === "panel" && (
          <PinForm intakeViewerRef={intakeViewerRef} mode="panel" pinMode={pinMode}>
            <CardFormFragment
              useFormCard={formCard}
              docPageCount={intake.pages}
              block={blockForForm}
              clusterType={currentLensHit?.clusterType}
              pageType={currentLensHit?.pageType}
              entity={processedCardItem?.entityOfItem}
              lensRuleDescription={currentLensHit?.ruleDescription}
              lensRuleSubtype={currentLensHit?.ruleSubtype}
              suggestedFormFieldId={currentLensHit?.formFieldId}
              showReferenceType={true}
              showHighlightedText={true}
              saveMultipleReferences
              pageHits={pageCache?.lensRun?.hits}
              showNotes
              {...props.pinForm?.cardFormFragmentProps}
              dataPendo="document-viewer-pin"
              actions={
                <Flex flexDirection="row" sx={{ paddingLeft: 2, mt: 2 }}>
                  {pinMode && (
                    <Button
                      data-pendo="document-viewer-pin-cancel"
                      color="primary"
                      size="small"
                      onClick={() => handlePinPage(false)}>
                      Cancel
                    </Button>
                  )}
                  <PinToProfileButton
                    pendo="document-viewer-pin-save"
                    formMode={pinMode}
                    canSave={formCard.canSave}
                    isSaving={formCard.savingFormState[0]}
                    toggleFormMode={toggleFormMode}
                  />
                </Flex>
              }
            />
          </PinForm>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            padding: 0.5,
            zIndex: pinMode ? theme.zIndex.modal : 0,
            backgroundColor: "white",
          }}>
          <Box display="flex" justifyContent="flex-start">
            <ZoomControls
              intakeId={intake.id}
              isImage={false}
              zoomControls={zoomControls}
              rotateCCW={rotate90CCW}
              pageProxy={pageProxy}
              docViewerSize={debouncedDocViewerSize}
            />
            <Flex centered>
              <Tooltip arrow title="Rotate">
                <IconButton onClick={rotate90CCW} size="medium" data-pendo="document-viewer-rotate-pdf">
                  <Rotate90DegreesCcw />
                </IconButton>
              </Tooltip>
            </Flex>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Paginator
              value={pageNum}
              setValue={(page) => goToPage(page)}
              min={1}
              max={intake.pages}
              bindArrowKeys={!pinMode}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            {foundReferenceOnCurrentPage && (
              <Tooltip title="Page in Profile" arrow>
                <CheckCircle sx={{ mr: 1, color: "grey.400" }} />
              </Tooltip>
            )}
            {isSupport && (
              <Tooltip arrow title="QA Mode">
                <IconButton
                  data-pendo="document-viewer-qamode"
                  name="document-viewer-qamode"
                  style={{ color: qaMode ? theme.palette.primary.main : undefined }}
                  onClick={() => setQAMode((prev) => !prev)}
                  size="medium">
                  <BiotechIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip arrow title="Draw Annotation">
              <IconButton
                data-pendo="document-viewer-draw-annotation"
                name="document-viewer-draw-annotation"
                style={{ color: annotations.drawing.enabled ? theme.palette.primary.main : undefined }}
                onClick={() => {
                  setHighlightLensHits(true);
                  annotations.setSelectedDocumentAnnotation(undefined);
                  annotations.drawing.setEnabled((prev) => !prev);
                }}
                size="medium">
                <Create />
              </IconButton>
            </Tooltip>
            {canShowAllHits === true && (
              <Tooltip arrow title="Show All Lens Hits">
                <IconButton
                  data-pendo="document-viewer-highlight-all-hits"
                  name="document-viewer-highlight-all-hits"
                  style={{ color: highlightLensHits ? theme.palette.primary.main : undefined }}
                  onClick={() => setHighlightLensHits(!highlightLensHits)}
                  size="medium">
                  <Visibility />
                </IconButton>
              </Tooltip>
            )}
            {canPin === true && props.canPinFullPage !== false && (
              <Tooltip arrow title="Pin To Profile">
                <IconButton
                  data-pendo="document-viewer-pin-full-page"
                  name="document-viewer-pin-to-profile"
                  style={{ color: pinMode ? theme.palette.primary.main : undefined }}
                  onClick={() => handlePinPage(!pinMode)}
                  size="medium">
                  <PushPinIcon />
                </IconButton>
              </Tooltip>
            )}
            {!docViewerScroll && (
              <Tooltip arrow title={showAnnotationSidebar ? "Hide Sidebar" : "Show Sidebar"}>
                <Badge
                  badgeContent={showAnnotationSidebar ? undefined : annotations.documentAnnotations.length}
                  color="primary"
                  // lower the badge
                  sx={{
                    "& .MuiBadge-badge": {
                      top: 4,
                      height: 16,
                    },
                  }}>
                  <IconButton
                    data-pendo="document-viewer-annotation-sidebar"
                    name="document-viewer-annotation-sidebar"
                    style={{ color: showAnnotationSidebar ? theme.palette.primary.main : undefined }}
                    onClick={() => {
                      annotations.handleClose();
                      setShowAnnotationSidebar((prev) => !prev);
                    }}
                    size="medium">
                    <ViewSidebar />
                  </IconButton>
                </Badge>
              </Tooltip>
            )}
            <IconButton onClick={(evt) => documentPopover.onPopoverOpen(evt.currentTarget)}>
              <MoreVert />
            </IconButton>
            <Popover
              open={documentPopover.isOpen}
              anchorEl={documentPopover.anchorEl}
              onClose={documentPopover.onPopoverClose}>
              <div className="flex flex-col py-4 gap-2">
                <div className="flex gap-2 px-4 items-center cursor-pointer" onClick={downloadIntake}>
                  <Download />
                  <p className="text-xs font-semibold">Download {intake.name}</p>
                </div>
                <div className="flex gap-2 px-4 pl-5 items-center">
                  <FormControlLabel
                    label="Save Zoom"
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={zoomControls.shouldSaveZoom}
                        onChange={(_, checked) => zoomControls.setShouldSaveZoom(checked)}
                      />
                    }
                  />
                </div>
              </div>
            </Popover>
          </Box>
        </Box>
        <Divider />
        {props.pinForm?.mode !== "panel" && (
          <Transition in={pinMode} timeout={1000}>
            {(state) => (
              <PinForm mode="drawer" state={state} pinMode={pinMode}>
                <CardFormFragment
                  useFormCard={formCard}
                  docPageCount={intake.pages}
                  block={blockForForm}
                  clusterType={currentLensHit?.clusterType}
                  pageType={currentLensHit?.pageType}
                  entity={processedCardItem?.entityOfItem}
                  lensRuleDescription={currentLensHit?.ruleDescription}
                  lensRuleSubtype={currentLensHit?.ruleSubtype}
                  suggestedFormFieldId={currentLensHit?.formFieldId}
                  showReferenceType={true}
                  showHighlightedText={true}
                  saveMultipleReferences
                  pageHits={pageCache?.lensRun?.hits}
                  actions={
                    <Flex flexDirection="row" sx={{ paddingX: 2 }}>
                      {pinMode && (
                        <Button
                          color="primary"
                          size="small"
                          onClick={() => handlePinPage(false)}
                          data-pendo="document-viewer-pin-cancel">
                          Cancel
                        </Button>
                      )}
                      <PinToProfileButton
                        pendo="document-viewer-pin-save"
                        formMode={pinMode}
                        canSave={formCard.canSave}
                        isSaving={formCard.savingFormState[0]}
                        toggleFormMode={toggleFormMode}
                      />
                    </Flex>
                  }
                />
              </PinForm>
            )}
          </Transition>
        )}
        <Box
          sx={{
            display: "flex",
            backgroundColor: "grey.100",
            height: "100%",
            width: "100%",
            minHeight: 0,
            zIndex: pinMode ? theme.zIndex.modal : 0,
          }}>
          <Box
            ref={docViewerRef}
            sx={{
              display: "flex",
              flexDirection: "column",
              zIndex: pinMode ? theme.zIndex.modal : 0,
              width: "100%",
              minWidth: 0,
            }}>
            {annotations.drawing.enabled && (
              <Box
                sx={{
                  backgroundColor: primary[400],
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                  px: 2,
                  py: 0.5,
                }}>
                <Box>
                  <P
                    variant="sm"
                    sx={{
                      color: "white",
                    }}>
                    <b>Annotate</b> - Click or drag on the document.
                  </P>
                </Box>
                <div className="flex items-center">
                  <div className="flex items-center">
                    <P
                      onClick={() => {
                        annotations.drawing.setMode((prev) => (prev === "free" ? "snap" : "free"));
                      }}
                      sx={{
                        cursor: "pointer",
                        color: "white",
                      }}>
                      Snap to text: {annotations.drawing.mode === "snap" ? "On" : "Off"}
                    </P>
                    {/* Not using PCheckbox as I want the default padding, size and no label. */}
                    <Checkbox
                      sx={{
                        color: "white",
                        "&.Mui-checked": {
                          color: "white",
                        },
                      }}
                      checked={annotations.drawing.mode === "snap"}
                      onChange={() => {
                        annotations.drawing.setMode((prev) => (prev === "free" ? "snap" : "free"));
                      }}
                    />
                  </div>

                  <Tooltip title="Exit Draw Tool" arrow>
                    <IconButton
                      data-pendo="document-viewer-draw-annotation-exit"
                      name="document-viewer-draw-annotation-exit"
                      sx={{
                        color: "white",
                      }}
                      onClick={() => annotations.drawing.setEnabled(false)}>
                      <Close />
                    </IconButton>
                  </Tooltip>
                </div>
              </Box>
            )}
            <div className="h-full overflow-auto focus:outline-0" ref={pdfContainerRefSet}>
              {renderDocument()}
            </div>
          </Box>
          {currentSegment && currentSegment.id && showAnnotationSidebar && (
            <AnnotationSidebar blocks={annotations.blocks} pageNumber={segmentPageNum} documentId={currentSegment.id} />
          )}
        </Box>
        {contextMenuCoords && clickedBlock && (
          <Popover
            open={contextMenuOpen}
            onClose={() => setContextMenuOpen(false)}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: contextMenuCoords.top,
              left: contextMenuCoords.left,
            }}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}>
            <List>
              <ListItem disablePadding>
                <ListItemButton
                  data-pendo="document-viewer-copy-to-clipboard"
                  onClick={() => {
                    navigator.clipboard.writeText(clickedBlock?.text);
                    setContextMenuOpen(false);
                    enqueueSnackbar("Copied to Clipboard", { variant: "info" });
                  }}>
                  <ListItemIcon>
                    <CopyAll />
                  </ListItemIcon>
                  <ListItemText primary="Copy to Clipboard" />
                </ListItemButton>
              </ListItem>
              {canPin === true && (
                <ListItem disablePadding>
                  <ListItemButton
                    data-pendo="document-viewer-pin-block-to-profile"
                    onClick={() => {
                      if (clickedBlock) {
                        setChosenBlock(clickedBlock);
                        setPinMode(true);
                        setContextMenuOpen(false);
                      }
                    }}>
                    <ListItemIcon>
                      <PushPin />
                    </ListItemIcon>
                    <ListItemText primary="Pin to Profile" />
                  </ListItemButton>
                </ListItem>
              )}
              {canPin === true && immediatePinToValueGroup !== undefined && (
                <ListItem disablePadding>
                  <ListItemButton
                    data-pendo="document-viewer-add-reference-block-to-response"
                    onClick={() => {
                      if (clickedBlock) {
                        immediatePinToValueGroup(clickedBlock);
                      }
                    }}>
                    <ListItemIcon>
                      <Add />
                    </ListItemIcon>
                    <ListItemText primary="Add to Entry" />
                  </ListItemButton>
                </ListItem>
              )}
              {hoveredAnnotation && (
                <ListItem disablePadding>
                  <ListItemButton
                    data-pendo="document-viewer-pin-block-to-profile"
                    onClick={() => {
                      // launch the popover for editing the annotation
                      annotations.setSelectedDocumentAnnotation(hoveredAnnotation);
                      annotations.setPopoverOpen(true);
                      setContextMenuOpen(false);
                    }}>
                    <ListItemIcon>
                      <Comment />
                    </ListItemIcon>
                    <ListItemText primary="Edit Annotation" />
                  </ListItemButton>
                </ListItem>
              )}
              {qaMode && (
                <ListItem>
                  <ListItemText primary={clickedBlock?.clusterType} />
                </ListItem>
              )}
            </List>
          </Popover>
        )}
        {currentSegment && contextMenuCoords && (
          <Popover
            open={annotations.popoverOpen}
            onClose={() => {
              if (annotations.drawing.boundingBox) {
                annotations.drawing.setBoundingBox(undefined);
              }
              annotations.setPopoverOpen(false);
              if (!annotations.drawing.enabled) {
                annotations.setSelectedDocumentAnnotation(undefined);
              }
            }}
            anchorReference="anchorPosition"
            anchorPosition={{
              top: contextMenuCoords.top,
              left: contextMenuCoords.left,
            }}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}>
            <Box
              sx={{
                px: 1,
                pt: 1,
                pb: 2,
              }}>
              <AnnotationEditor
                blocks={annotations.blocks}
                pageNumber={segmentPageNum}
                documentId={currentSegment.id}
              />
            </Box>
          </Popover>
        )}
      </div>
    </AnnotationsContext.Provider>
  );
};

export default IntakeViewer;
